import React from 'react';
import { Users, Clock, Search, Shield, Folder, ClipboardList, User } from 'lucide-react';
import styles from './UserEngagementMetrics.module.css';
import { UserEngagementMetricsData } from '../../../../api/adminApi';

interface UserEngagementMetricsProps {
  isLoading?: boolean;
  data?: UserEngagementMetricsData;
}

interface StatItem {
  label: string;
  description: string;
  value: string;
  trend: number;
  icon: React.ReactNode;
}

const userStats = (data?: UserEngagementMetricsData): StatItem[] => [
  {
    label: 'Active Users',
    description: 'Unique users who logged in',
    value: data ? data.daily_active_users.value.toLocaleString() : '0',
    trend: data ? data.daily_active_users.trend : 0,
    icon: <Users size={20} aria-hidden />,
  },
  {
    label: 'Avg. Session Duration',
    description: 'Login → logout time',
    value: data ? data.avg_session_duration.value : '0m 0s',
    trend: data ? data.avg_session_duration.trend : 0,
    icon: <Clock size={20} aria-hidden />,
  },
  {
    label: 'Queries per User',
    description: 'Avg chat messages sent',
    value: data ? String(data.searches_per_user.value) : '0',
    trend: data ? data.searches_per_user.trend : 0,
    icon: <Search size={20} aria-hidden />,
  },
];

const adminStats = (data?: UserEngagementMetricsData): StatItem[] => [
  {
    label: 'Active Admins',
    description: 'Unique admins who logged in',
    value: data ? data.admin.active_admins.value.toLocaleString() : '0',
    trend: data ? data.admin.active_admins.trend : 0,
    icon: <Shield size={20} aria-hidden />,
  },
  {
    label: 'Avg. Session Duration',
    description: 'Admin login → logout time',
    value: data ? data.admin.avg_session_duration.value : '0m 0s',
    trend: data ? data.admin.avg_session_duration.trend : 0,
    icon: <Clock size={20} aria-hidden />,
  },
  {
    label: 'Total Uploads',
    description: 'All uploaded documents',
    value: data ? data.admin.total_actions.value.toLocaleString() : '0',
    trend: data ? data.admin.total_actions.trend : 0,
    icon: <Folder size={20} aria-hidden />,
  },
  {
    label: 'Uploads per Admin',
    description: 'Avg uploaded docs',
    value: data ? String(data.admin.actions_per_admin.value) : '0',
    trend: data ? data.admin.actions_per_admin.trend : 0,
    icon: <ClipboardList size={20} aria-hidden />,
  },
];

const Stat: React.FC<StatItem> = ({ label, description, value, trend, icon }) => (
  <div className={styles.stat}>
    <div className={styles.statIcon}>{icon}</div>
    <div className={styles.statBody}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statDesc}>{description}</div>
      <div className={styles.statTrend}>
        <span className={trend >= 0 ? styles.trendUp : styles.trendDown}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
        </span>
        <span className={styles.trendVs}>vs prev 30d</span>
      </div>
    </div>
  </div>
);

const SkeletonStat: React.FC = () => <div className={`${styles.stat} ${styles.skeleton}`} />;

export const UserEngagementMetrics: React.FC<UserEngagementMetricsProps> = ({
  isLoading = false,
  data,
}) => (
  <div className={styles.container}>
    {/* Users */}
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}><User size={18} aria-hidden /></span>
        <span className={styles.sectionTitle}>User Engagement</span>
        <span className={styles.sectionPeriod}>Last 30 days</span>
      </div>
      <div className={styles.statsRow}>
        {isLoading
          ? [1, 2, 3].map((i) => <SkeletonStat key={i} />)
          : userStats(data).map((s) => <Stat key={s.label} {...s} />)}
      </div>
    </div>

    <div className={styles.divider} />

    {/* Admins */}
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}><Shield size={18} aria-hidden /></span>
        <span className={styles.sectionTitle}>Admin Engagement</span>
        <span className={styles.sectionPeriod}>Last 30 days</span>
      </div>
      <div className={styles.statsRow}>
        {isLoading
          ? [1, 2, 3, 4].map((i) => <SkeletonStat key={i} />)
          : adminStats(data).map((s) => <Stat key={s.label} {...s} />)}
      </div>
    </div>
  </div>
);
